import React, { useEffect, useState } from 'react'
import {
  FaMountain,
  FaSearch,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaStar,
  FaFilter,
  FaLeaf,
  FaBolt,
  FaExclamationTriangle,
} from 'react-icons/fa'
import activityService from '../services/activityService'
import Loader from '../components/Loader'
import { formatPrice } from '../utils/helpers'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import SEO from '../utils/SEO'
import Button from '../components/ui/Button'

const difficultyConfig = {
  easy: {
    label: 'Easy',
    color: 'bg-green-100 text-green-800',
    icon: <FaLeaf className="inline mr-1" />,
  },
  moderate: {
    label: 'Moderate',
    color: 'bg-yellow-100 text-yellow-800',
    icon: <FaStar className="inline mr-1" />,
  },
  hard: {
    label: 'Challenging',
    color: 'bg-red-100 text-red-800',
    icon: <FaBolt className="inline mr-1" />,
  },
  extreme: {
    label: 'Extreme',
    color: 'bg-purple-100 text-purple-800',
    icon: <FaExclamationTriangle className="inline mr-1" />,
  },
}

const Adventures = () => {
  const [adventures, setAdventures] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ search: '', difficulty: '', location: '' })
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    loadAdventures()
  }, [])

  const loadAdventures = async (filterParams = {}) => {
    try {
      setLoading(true)
      const params = {}
      if (filterParams.difficulty) params.difficulty = filterParams.difficulty
      if (filterParams.location) params.location = filterParams.location

      const data = await activityService.getAllActivities(params)
      let results = data.activities || []

      // Client-side search filter
      if (filterParams.search) {
        const q = filterParams.search.toLowerCase()
        results = results.filter(
          (a) =>
            a.name?.toLowerCase().includes(q) ||
            a.description?.toLowerCase().includes(q) ||
            a.location?.toLowerCase().includes(q),
        )
      }

      setAdventures(results)
    } catch (error) {
      toast.error('Error loading adventures')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const applyFilters = () => {
    loadAdventures(filters)
  }

  const clearFilters = () => {
    const reset = { search: '', difficulty: '', location: '' }
    setFilters(reset)
    loadAdventures(reset)
  }

  const getDifficultyBadge = (difficulty) => {
    const config = difficultyConfig[difficulty] || difficultyConfig.moderate
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}
      >
        {config.icon}
        {config.label}
      </span>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Adventure Activities in Himachal Pradesh"
        description="Book thrilling adventure activities in the Himalayas. Trekking, paragliding, river rafting, camping, skiing and more across Shimla, Manali, Bir Billing, Kullu, and Spiti Valley."
        canonical="/adventures"
        keywords="adventure activities Himachal Pradesh, trekking Himachal, paragliding Bir Billing, river rafting Kullu, camping Manali, skiing Solang Valley"
      />
      <ToastContainer />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-700 to-indigo-700 text-white py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1440 320" className="w-full h-full" preserveAspectRatio="none">
            <path
              fill="#fff"
              d="M0,160L80,138.7C160,117,320,75,480,80C640,85,800,139,960,160C1120,181,1280,171,1360,165.3L1440,160L1440,320L0,320Z"
            />
          </svg>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <FaMountain className="text-6xl mx-auto mb-4 text-blue-300" />
          <h1 className="text-5xl font-bold mb-4">Himalayan Adventures</h1>
          <p className="text-blue-100 text-xl max-w-2xl mx-auto">
            Conquer peaks, traverse valleys, and experience the raw magnificence of the Himalayas
            with our curated adventure activities.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search & Filters */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6 mb-10">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="Search adventures..."
                className="input-field !pl-10"
              />
            </div>

            <Button
              variant="secondary"
              icon={<FaFilter />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>

            <Button onClick={applyFilters}>Search</Button>
          </div>

          {showFilters && (
            <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/30">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select
                  name="difficulty"
                  value={filters.difficulty}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Difficulties</option>
                  <option value="easy">Easy</option>
                  <option value="moderate">Moderate</option>
                  <option value="hard">Challenging</option>
                  <option value="extreme">Extreme</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  placeholder="e.g. Manali, Spiti..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear all filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Adventures Grid */}
        {loading ? (
          <Loader />
        ) : adventures.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <FaMountain className="text-6xl mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold mb-2">No Adventures Found</h3>
            <p>Try adjusting your filters or check back soon for new adventures.</p>
            {(filters.search || filters.difficulty || filters.location) && (
              <button onClick={clearFilters} className="mt-4 link-primary">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="text-gray-500 mb-6">
              Showing <span className="font-semibold text-gray-900">{adventures.length}</span>{' '}
              adventure{adventures.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {adventures.map((adventure) => (
                <div key={adventure.id} className="card group">
                  {/* Image */}
                  <div className="relative h-52 bg-gradient-to-br from-blue-400 to-indigo-600 overflow-hidden shine-on-hover">
                    {adventure.image_url ? (
                      <img
                        src={adventure.image_url}
                        alt={adventure.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <FaMountain className="text-white text-6xl opacity-40" />
                      </div>
                    )}
                    {adventure.difficulty && (
                      <div className="absolute top-3 left-3">
                        {getDifficultyBadge(adventure.difficulty)}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{adventure.name}</h3>
                    {adventure.description && (
                      <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                        {adventure.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-4">
                      {adventure.location && (
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt className="text-blue-500" /> {adventure.location}
                        </span>
                      )}
                      {adventure.duration && (
                        <span className="flex items-center gap-1">
                          <FaClock className="text-green-500" /> {adventure.duration}
                        </span>
                      )}
                      {adventure.max_participants && (
                        <span className="flex items-center gap-1">
                          <FaUsers className="text-purple-500" /> Max {adventure.max_participants}
                        </span>
                      )}
                      {adventure.min_age > 0 && (
                        <span className="flex items-center gap-1 text-orange-600 font-medium">
                          Age {adventure.min_age}+
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        <span className="text-2xl font-bold text-blue-600">
                          {formatPrice(parseFloat(adventure.price) || 0)}
                        </span>
                        <span className="text-gray-400 text-sm ml-1">/ person</span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          // Store adventure as preselected for booking
                          localStorage.setItem(
                            'preSelectedService',
                            JSON.stringify({
                              id: adventure.id,
                              name: adventure.name,
                              type: 'activity',
                            }),
                          )
                          window.location.href = '/tours'
                        }}
                      >
                        Reserve Your Spot
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Adventures
